import type { DraftAbilityDefinition } from "../domain/types";

const ability = (
  towerId: string,
  name: string,
  description: string,
  effects: DraftAbilityDefinition["effects"],
): DraftAbilityDefinition => ({ id: `${towerId}-ability`, towerId, name, description, effects });

export const TOWER_ABILITIES: Readonly<Record<string, DraftAbilityDefinition>> = Object.freeze({
  archer: ability("archer", "Arrow Storm", "Fires faster and gains an extra target.", {
    intervalMultiplier: 0.88,
    targetBonus: 1,
  }),
  rocket: ability("rocket", "Cluster Salvo", "Explosions become wider and more damaging.", {
    damageMultiplier: 1.14,
    splashBonus: 0.45,
  }),
  lightning: ability(
    "lightning",
    "Storm Conductor",
    "Lightning chains farther and strikes harder.",
    { damageMultiplier: 1.12, targetBonus: 1 },
  ),
  cannon: ability("cannon", "Castle Cracker", "Massive shells gain damage and armor break.", {
    damageMultiplier: 1.18,
    armorBreakBonus: 0.08,
  }),
  crossbow: ability("crossbow", "Bodkin Volley", "Bolts gain range and precision damage.", {
    damageMultiplier: 1.15,
    rangeBonus: 0.45,
  }),
  ballista: ability(
    "ballista",
    "Dragon Piercer",
    "Extreme-range shots deal greatly increased damage.",
    { damageMultiplier: 1.2, rangeBonus: 0.5 },
  ),
  frost: ability("frost", "Whiteout", "The frost field grows and pulses faster.", {
    intervalMultiplier: 0.86,
    splashBonus: 0.5,
  }),
  flame: ability("flame", "Living Inferno", "Fire damage rises and the cone widens.", {
    damageMultiplier: 1.15,
    splashBonus: 0.4,
  }),
  poison: ability("poison", "Plague Bloom", "Venom attacks faster and reaches farther.", {
    intervalMultiplier: 0.88,
    rangeBonus: 0.4,
  }),
  earthshaker: ability(
    "earthshaker",
    "Seismic Chain",
    "Ground pulses cover more area and hit harder.",
    { damageMultiplier: 1.16, splashBonus: 0.55 },
  ),
  time: ability("time", "Chrono Lock", "Temporal fields cover a larger area and cycle faster.", {
    intervalMultiplier: 0.84,
    splashBonus: 0.55,
  }),
  alchemist: ability("alchemist", "Royal Solvent", "Flasks strip more armor and splash farther.", {
    armorBreakBonus: 0.1,
    splashBonus: 0.4,
  }),
  banner: ability("banner", "King's Muster", "The banner's support radius expands dramatically.", {
    rangeBonus: 0.85,
    splashBonus: 0.6,
  }),
  "scout-tower": ability(
    "scout-tower",
    "Watchfire Network",
    "Reveals stealth for nearby towers and gains range.",
    { rangeBonus: 0.8, revealAuraBonus: 1.5 },
  ),
  sniper: ability("sniper", "Perfect Shot", "Critical shots gain immense damage.", {
    damageMultiplier: 1.24,
    rangeBonus: 0.35,
  }),
  execution: ability(
    "execution",
    "Final Judgment",
    "Finishing attacks become faster and stronger.",
    { damageMultiplier: 1.17, intervalMultiplier: 0.9 },
  ),
  bombard: ability(
    "bombard",
    "Rolling Thunder",
    "Artillery gains blast radius and faster reloads.",
    { intervalMultiplier: 0.9, splashBonus: 0.7 },
  ),
  arcane: ability("arcane", "Mana Fracture", "Arcane bolts gain damage and an additional target.", {
    damageMultiplier: 1.14,
    targetBonus: 1,
  }),
  gold: ability("gold", "Royal Dividend", "Income cycles faster and its influence expands.", {
    intervalMultiplier: 0.82,
    rangeBonus: 0.5,
  }),
  dragon: ability(
    "dragon",
    "Ancient Awakening",
    "Dragon fire gains damage, reach, and wider coverage.",
    { damageMultiplier: 1.18, rangeBonus: 0.35, splashBonus: 0.45 },
  ),
});
